/* eslint-disable max-lines */
/**
 * Validation service
 */
import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import * as Ajv from 'ajv';
import * as moment from 'moment';
import {
  every,
  filter,
  find,
  get,
  has,
  head,
  includes,
  intersection,
  isArray,
  isBoolean,
  isEmpty,
  isEqual,
  isFinite,
  isFunction,
  isNaN,
  isNil,
  isNull,
  isString,
  pick,
  some,
  startCase,
  sum,
  toNumber,
  toPairs,
  trim,
  uniqBy,
  values,
  gte,
} from 'lodash';
import {
  FORM_ERROR_SEPARATOR,
  FORM_ROOT_PATH,
  TIME_FORMATS,
  WEEK_DAYS,
} from '@config/index';
import { IErrorDefinition, IValidationError } from '@shared/types';
import { IFormGroupRules, IFormRules } from '@components/form/services';
import { XVIError } from '@shared/models';
import { rx, Subscription } from '@shared/rx';
import {
  ALERT_EVERY_MINUTE_AND_ORDER_ENTITY_LEAD_TIME_COMPARING_ERROR,
  JOB_CODES_UNIQUENESS_ERROR,
  ORDER_ENTITY_LEAD_TIME_AND_OVERDUE_TIME_COMPARING_ERROR,
} from '@text-content/messages';
import { getFormFieldValue } from '@components/form/services/schema-form/schema-form-functions';
import {
  arrayHasEmptyValues,
  isCloseToInteger,
} from '@app/shared/utils/common-utils';
import { getDecimalPlaces } from '@app/shared/utils/number-utils';
import { toMoment } from '@shared/utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

export type ValidationPredicate = (value: any) => boolean;
export type ValidationErrorsFn = (value: any) => ValidationErrors;
export type ValidationErrorParam =
  | ValidationErrors
  | ValidationErrorsFn
  | string;
export type ComparatorFn = (actual: any, expected: any) => boolean;
export interface IBinaryFileType {
  filename: string;
  filetype: string;
  binaryFileType: string;
  hex: string;
}

const IP_V4_PATTERN = /^(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/;
const URL_PATTERN =
  /^https?:\/\/((www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}|([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4}|(\d{1,3}\.){3}\d{1,3})\b[-a-zA-Z0-9@:%_\+.~#?&\/=]*|^$/i;
const LANGUAGE_CODE_PATTERN = /^[a-zA-Z](-?[a-zA-Z])*$/;

/**
 * Converts JSON pointer to a FormControl path accessor array
 * e.g. 2/some/field to [parent,parent,controls,some,controls,field,value]
 */
const jsonPointerPath: Function = (pointer): any[] => {
  const [level, ...path] = pointer.split('/');

  return []
    .concat(level === '' ? 'root' : Array(+level).fill('parent'))
    .concat(...path.map((node) => ['controls', node]), 'value');
};

/**
 * @param input - a static sample, function or a subject to which control value is to be compared
 * @param control - current control, that may be used to compose messages or validators
 */
const dynamicResolver: Function = (input: any, control?: FormControl): any => {
  if (isFunction(input)) {
    return input(control);
  }
  // subject-like object or formControl
  if (has(input, 'observers')) {
    return input.value;
  }

  if (has(input, 'errorMessage') && has(input, 'value')) {
    return input.value;
  }

  if (has(input, '$data')) {
    const path: any[] = jsonPointerPath(input.$data);

    return get(control, path);
  }

  return input;
};

const errorResolver: Function = (input: any, actual?, expected?): any => {
  return isFunction(input) ? input(actual, expected) : input;
};

const validateControlValue: Function = ({
  actual,
  expected,
  comparator,
}: {
  actual: any;
  expected: any;
  comparator: ComparatorFn;
}): boolean => {
  return comparator(actual, expected);
};

@Injectable()
export class ValidationService {
  public messages: any = {
    emailValidationError: this.i18nService.translate(
      I18n.ErrorMessages.EmailIsNotValid,
    ),
    invalidURL: this.i18nService.translate(
      I18n.ErrorMessages.UrlFormatIsNotCorrect,
    ),
    invalidLanguageCode: this.i18nService.translate(
      I18n.ErrorMessages.InvalidLanguageCode,
    ),
    minValue: (actual: string, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldBeAtLeastExpected,
        {
          expected,
        },
      ),
    maxValue: (actual: string, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldNotBeGreaterThanExpected,
        { expected },
      ),
    strictMinValue: (actual: string, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldBeMoreThanExpected,
        {
          expected,
        },
      ),
    strictMaxValue: (actual: string, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldBeLessThanExpected,
        {
          expected,
        },
      ),
    minTimeValue: (actual: string, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldBeAtLeastExpected,
        {
          expected: ValidationService.formatTime(expected),
        },
      ),
    maxTimeValue: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldNotBeGreaterThanExpected,
        {
          expected: ValidationService.formatTime(expected),
        },
      ),
    minDateValue: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldBeAtLeastExpected,
        {
          expected: toMoment(expected).format(TIME_FORMATS.L),
        },
      ),
    maxDateValue: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ValueShouldNotBeGreaterThanExpected,
        {
          expected: toMoment(expected).format(TIME_FORMATS.L),
        },
      ),
    invalidYear: this.i18nService.translate(I18n.ErrorMessages.YearIsNotValid),
    invalidTime: this.i18nService.translate(I18n.ErrorMessages.TimeIsNotValid),
    minLength: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.FieldNeedsToBeAtLeastExpected,
        {
          expected,
          character: this.i18nService.t(I18n.Common.character),
          characters: this.i18nService.t(I18n.Common.characters),
        },
      ),
    maxLength: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.FieldNeedsToBeNoLongerThanExpected,
        {
          expected,
          character: this.i18nService.t(I18n.Common.character),
          characters: this.i18nService.t(I18n.Common.characters),
        },
      ),
    trimmedMinLength: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.FieldNeedsToBeAtLeastExpectedWithTrim,
        {
          expected,
          character: this.i18nService.t(I18n.Common.character),
          characters: this.i18nService.t(I18n.Common.characters),
        },
      ),
    minArrayLength: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ArrayFieldNeedsToBeAtLeastExpected,
        {
          expected,
          element: this.i18nService.t(I18n.Common.element),
          elements: this.i18nService.t(I18n.Common.elements),
        },
      ),
    maxArrayLength: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.ArrayFieldNeedsToBeNoLongerThanExpected,
        {
          expected,
          element: this.i18nService.t(I18n.Common.element),
          elements: this.i18nService.t(I18n.Common.elements),
        },
      ),
    invalidJsonDataParse: this.i18nService.translate(
      I18n.ErrorMessages.JsonParseIsNotValid,
    ),
    invalidJsonDataCompile: this.i18nService.translate(
      I18n.ErrorMessages.JsonCompileIsNotValid,
    ),
    invalidNumericValue: (): string =>
      this.i18nService.translate(I18n.ErrorMessages.InvalidNumericValue),
    greaterThanDecimalPlacesValue: (actual, expected): string =>
      this.i18nService.instant(
        I18n.ErrorMessages.EnterAnAmountWithUpToDecimalPlaces,
        {
          decimalPlaces: getDecimalPlaces(expected),
        },
      ),
    required: this.i18nService.instant(I18n.ErrorMessages.FieldIsRequired),
    invalidDateFormatValue: this.i18nService.translate(
      I18n.ErrorMessages.InvalidDateSelected,
    ),
    atLeastOneFieldRequired: (actual, expected): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.AtLeastOneFieldOfExpectedShouldBeFilledIn,
        { expected },
      ),
    requiredValues: (actual, expected): string =>
      this.i18nService.translate(I18n.ErrorMessages.ValuesShouldBeSelected, {
        expected: expected.join(', '),
      }),
    requiredValue: this.i18nService.translate(
      I18n.ErrorMessages.ValueShouldBeSelected,
    ),
    oneOf: this.i18nService.instant(I18n.ErrorMessages.InvalidValue),
    notOneOf: this.i18nService.translate(I18n.ErrorMessages.NotUnique),
    contains: this.i18nService.translate(I18n.ErrorMessages.InvalidValue),
    notContains: (actual, expected): string =>
      this.i18nService.translate(I18n.ErrorMessages.FollowingItemsNotAllowed, {
        items: intersection(actual, expected).join(', '),
      }),
    equalTo: this.i18nService.translate(I18n.ErrorMessages.InvalidValue),
    notEqualTo: this.i18nService.translate(I18n.ErrorMessages.InvalidValue),
    valueType: (actual, expected): string =>
      this.i18nService.translate(I18n.ErrorMessages.TypeMismatchError, {
        actual,
        expected,
      }),
    wrongDates: (row): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.RowStartDateShouldBeLessThanEndDate,
        { row },
      ),
    ipAddress: this.i18nService.translate(
      I18n.ErrorMessages.IPAddressIsNotValid,
    ),
    invalidNutritionalRangeValues: this.i18nService.translate(
      I18n.ErrorMessages.CheckHighlightedMinAndMaxValueRows,
    ),
    invalidNutritionalValues: this.i18nService.translate(
      I18n.ErrorMessages.HighlightedRowsValueShouldEqualZeroOrGreater,
    ),
    openCloseTimesValidation: (): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.OpenTimeCanNotBeGreaterThanCloseTime,
      ),
    startEndTimeValidator: (): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.EndTimeMustGreaterThanStartTime,
      ),
    startEndTimeRequiredValidator: (): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.PeriodNeedsToHaveBothStartAndEndTime,
      ),
    startEndDaysValidator: (): string =>
      this.i18nService.translate(
        I18n.ErrorMessages.StartDateCannotGreaterThanEndDate,
      ),
    uniqInCollectionValidator: (actual, key): string =>
      this.i18nService.translate(I18n.ErrorMessages.KeyShouldBeUnique, { key }),
    timeRangeOverlappingValidator: this.i18nService.translate(
      I18n.ErrorMessages.DaypartsOverlap,
    ),
    timePeriodContainsDaysOfWeekValidator: this.i18nService.translate(
      I18n.ErrorMessages.TimePeriodDaysNotContained,
    ),
    urlAddress: this.i18nService.translate(
      I18n.ErrorMessages.UrlFormatIsNotCorrect,
    ),
    dayFillingValidator: this.i18nService.translate(
      I18n.ErrorMessages.DaypartsAreNotContiguous,
    ),
    patternValidator: this.i18nService.translate(
      I18n.ErrorMessages.ValueDoesNotMatchRegularExpression,
    ),
    /**
     * @deprecated this is not used?
     */
    receiverAlertEveryMaxValidator:
      ALERT_EVERY_MINUTE_AND_ORDER_ENTITY_LEAD_TIME_COMPARING_ERROR,
    /**
     * @deprecated this is not used?
     */
    receiverOverdueMaxValidator:
      ORDER_ENTITY_LEAD_TIME_AND_OVERDUE_TIME_COMPARING_ERROR,
    specificValidationServerError: this.i18nService.translate(
      I18n.ErrorMessages.NotAbleToValidateField,
    ),
    /**
     * @deprecated this is not used?
     */
    singleJobCode: JOB_CODES_UNIQUENESS_ERROR,
    invalidQuantityPriceRangeEnd: this.i18nService.translate(
      I18n.ErrorMessages.RangeEndCannotBeLessThanRangeStart,
    ),
    uniqValues: this.i18nService.translate(
      I18n.ErrorMessages.ValueShouldBeUnique,
    ),
    emptyValues: this.i18nService.translate(
      I18n.ErrorMessages.PleaseAddAtLeastOneOption,
    ),
    timePeriodMinLength: this.i18nService.translate(
      I18n.ErrorMessages.PeriodTypeOptionsShouldBeSelected,
    ),
    loyaltyCustomerIdentificationMethod: this.i18nService.translate(
      I18n.ErrorMessages.PleaseAssignDefaultIdentificationMethod,
    ),
  };

  public static PATTERNS: { [key: string]: string } = {
    /*
     * STRING_WITHOUT_SPECIAL_CHARACTERS
     * What is accepted:
     *  - alphanumeric both capital and lowercase, including
     *      extended Latin (ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏàáâãäåæçèéêëìíîïÐÑÒÓÔÕÖØÙÚÛÜÝÞßðñòóôõöøùúûüýþÿ),
     *      cyrillic letters
     *  - multiple words with spaces between them
     *  - period, comma, dash and apostrophe:  . , - '
     * What is not accepted:
     *  - special characters: @ # $ % ^ & * ( ) ! ? / | \ ~ ` "
     */
    STRING_WITHOUT_SPECIAL_CHARACTERS: '^([^@#$%^&*()!?/|\\\\~`"]*)$',
    STRING_WITHOUT_SPECIAL_CHARACTERS_AND_SPACES:
      '^([^@#$%^&*()!?/|\\\\~`"\\s]*)$',
    STRING_WITHOUT_SPECIAL_CHARACTERS_AND_SPACES_ONLY_LATIN_NUMBER_DASHES:
      '^[a-zA-Z0-9-_]+$',
    STRING_WITHOUT_SPECIAL_CHARACTERS_WITH_DASH_AND_UNDERSCORE:
      '[a-zA-Z0-9-_\\s]*',
    STRING_WITHOUT_SPECIAL_CHARACTERS_AND_NUMBERS_WITH_DASH_UNDERSCORE_AND_SPACES:
      '[a-zA-Z-_\\s]*',
    STRING_WITHOUT_SPECIAL_CHARACTERS_AND_SPACES_ONLY_LATIN_NUMBER_DASHES_SLASH:
      '^[a-zA-Z0-9-_/]+$',
  };

  public static decimalPrecisionComparator(
    actual: number,
    expected: number,
  ): boolean {
    return (
      isNull(actual) ||
      (isFinite(actual) && isCloseToInteger(actual / expected))
    );
  }

  public ajv: Ajv.Ajv;

  public createValidator(
    errorType: string,
    comparator: ComparatorFn,
  ): any | null {
    return (
      inputSample?,
      errorMessage = get(inputSample, 'errorMessage', this.messages[errorType]),
    ): any => {
      return (control): any => {
        const expected: any = dynamicResolver(inputSample, control);
        const actual: any = control.value;

        const isValid: boolean = validateControlValue({
          actual,
          expected,
          comparator,
        });

        return isValid
          ? null
          : { [errorType]: errorResolver(errorMessage, actual, expected) };
      };
    };
  }

  public requiredValidator: ValidatorFn = this.createValidator(
    'required',
    (value) => !Validators.required({ value } as AbstractControl),
  )();

  public dateFormatValidator: Function = this.createValidator(
    'invalidDateFormatValue',
    (actual, expected) => moment(actual, expected).format(expected) === actual,
  );

  public minValidator: Function = this.createValidator(
    'minValue',
    (actual, expected) =>
      isNil(actual) || isNaN(actual) || isNull(expected) || actual >= expected,
  );

  public maxValidator: Function = this.createValidator(
    'maxValue',
    (actual, expected) =>
      isNil(actual) || isNull(expected) || actual <= expected,
  );

  public languageCodeValidator: Function = this.createValidator(
    'invalidLanguageCode',
    (actual) => (isEmpty(actual) ? true : LANGUAGE_CODE_PATTERN.test(actual)),
  );

  public timeValidator: Function = this.createValidator(
    'invalidTime',
    (actual) =>
      isFunction(get(actual, 'getHours')) && !isNaN(actual.getHours()),
  );

  public yearValidator: Function = this.createValidator(
    'invalidYear',
    (actual) => actual && actual.match(/^([0-9]\d*){4}$/i),
  )();

  public emailValidator: ValidatorFn = this.createValidator(
    'emailValidationError',
    (value) => !Validators.email({ value } as AbstractControl),
  )();

  public strictMinValidator: Function = this.createValidator(
    'strictMinValue',
    (actual, expected) => isNull(actual) || actual > expected,
  );

  public strictMaxValidator: Function = this.createValidator(
    'strictMaxValue',
    (actual, expected) => isNull(actual) || actual < expected,
  );

  public minTimeValidator: Function = this.createValidator(
    'minTimeValue',
    (actual, expected) =>
      actual === '' || expected === '' || actual >= expected,
  );

  public maxTimeValidator: Function = this.createValidator(
    'maxTimeValue',
    (actual, expected) =>
      actual === '' || expected === '' || actual <= expected,
  );

  public minArrayLength: Function = this.createValidator(
    'minArrayLength',
    (actual, expected) => !actual || actual.length >= expected,
  );

  public maxArrayLength: Function = this.createValidator(
    'maxArrayLength',
    (actual, expected) => !actual || actual.length <= expected,
  );

  public minDateValidator: Function = this.createValidator(
    'minDateValue',
    (actual, expected) =>
      actual === '' || expected === '' || actual >= expected,
  );

  public maxDateValidator: Function = this.createValidator(
    'maxDateValue',
    (actual, expected) =>
      actual === '' || expected === '' || actual <= expected,
  );

  public minLength: Function = this.createValidator(
    'minLength',
    (actual, expected) => isEmpty(actual) || actual.length >= expected,
  );

  public trimmedMinLength: Function = this.createValidator(
    'trimmedMinLength',
    (actual, expected) => {
      const trimmedString = trim(actual);

      return (
        isEmpty(actual) ||
        !isEmpty(trimmedString) ||
        trimmedString.length >= expected
      );
    },
  );

  public maxLength: Function = this.createValidator(
    'maxLength',
    (actual, expected) => isEmpty(actual) || actual.length <= expected,
  );

  public oneOf: Function = this.createValidator('oneOf', (actual, expected) => {
    if (isArray(actual) && !isEmpty(actual)) {
      return every(actual, (item) => includes(expected, item));
    }

    return expected.some(isEqual.bind(null, actual));
  });

  public notOneOf: Function = this.createValidator(
    'notOneOf',
    (actual, expected) => !expected.some(isEqual.bind(null, actual)),
  );

  public timePeriodMinLength: Function = this.createValidator(
    'timePeriodMinLength',
    (actual, expected) => !actual || actual.length >= expected,
  );

  public ipAddress: Function = this.createValidator('ipAddress', (actual) =>
    !isString(actual) && !isNil(actual) ? false : IP_V4_PATTERN.test(actual),
  );

  public contains: Function = this.createValidator(
    'contains',
    (actual, expected) => {
      let valueIsValid: boolean = true;

      actual.forEach((val) => {
        if (valueIsValid && !expected.includes(val)) {
          valueIsValid = false;
        }
      });

      return valueIsValid;
    },
  );

  public notContains: Function = this.createValidator(
    'notContains',
    (actual, expected) => isEmpty(intersection(actual, expected)),
  );

  public equalTo: Function = this.createValidator(
    'equalTo',
    (actual, expected) => actual === expected,
  );

  public notEqualTo: Function = this.createValidator(
    'notEqualTo',
    (actual, expected) => actual !== expected,
  );

  public requiredValuesValidator: Function = this.createValidator(
    'requiredValues',
    (actual, expected) =>
      isNull(actual) || (expected || []).every((c) => actual.includes(c)),
  );

  public requiredValueValidator: Function = this.createValidator(
    'requiredValue',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (actual, expected) => !!actual,
  );

  public valueType: Function = this.createValidator(
    'valueType',
    (actual, expected) => {
      const controlValueType: string = Array.isArray(actual)
        ? 'array'
        : typeof actual;

      return controlValueType === expected;
    },
  );

  public numberValidator: Function = this.createValidator(
    'invalidNumericValue',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (actual, expected) => isNull(actual) || isFinite(actual),
  );

  public numberMultipleOfValidator: Function = this.createValidator(
    'invalidNumericValue',
    (actual, expected) =>
      isNull(actual) ||
      (isFinite(actual) && isCloseToInteger(actual / expected)),
  );

  public greaterThanDecimalPlacesValidator: Function = this.createValidator(
    'greaterThanDecimalPlacesValue',
    (actual, expected) =>
      isNull(actual) ||
      (isFinite(actual) && this.isEffectivePrecisionNumber(actual, expected)),
  );

  public uniqInCollectionValidator: Function = this.createValidator(
    'uniqInCollectionValidator',
    (collection, key) => uniqBy(collection, key).length === collection.length,
  );

  public startEndValidator: Function = this.createValidator(
    'openCloseTimesValidation',
    (actual) =>
      actual &&
      !actual.some(
        (config) => get(config, 'open_time', 0) > get(config, 'close_time', 0),
      ),
  );

  public startEndTimeValidator: Function = this.createValidator(
    'startEndTimeValidator',
    (actual) =>
      actual &&
      actual.every((config) => {
        const startTime = config.start_time;
        const endTime = config.end_time;

        return (
          (isNull(startTime) && isNull(endTime)) ||
          (!isNull(startTime) && !isNull(endTime) && startTime < endTime)
        );
      }),
  );

  public startEndTimeRequiredValidator: Function = this.createValidator(
    'startEndTimeRequiredValidator',
    (actual) =>
      actual &&
      actual.every((config) => {
        const startTime = config.start_time;
        const endTime = config.end_time;

        return (
          (isEmpty(startTime) && isEmpty(endTime)) ||
          (!isEmpty(startTime) && !isEmpty(endTime))
        );
      }),
  );

  public startEndDaysValidator: Function = this.createValidator(
    'startEndDaysValidator',
    (actual) =>
      actual &&
      actual.every((config) => {
        const startDate =
          config.start_date && toMoment(config.start_date).utc();
        const endDate = config.end_date && toMoment(config.end_date).utc();

        return (
          (!startDate && !endDate) ||
          (startDate && endDate && startDate <= endDate)
        );
      }),
  );

  public patternValidator: Function = this.createValidator(
    'patternValidator',
    (actual, pattern) =>
      !Validators.pattern(pattern)({ value: actual } as FormControl),
  );

  public timePeriodContainsDaysOfWeekValidator: Function = this.createValidator(
    'timePeriodContainsDaysOfWeekValidator',
    (collection) =>
      collection &&
      !collection.some((config) => {
        const startDate =
          config.start_date && toMoment(config.start_date).utc();
        const endDate = config.end_date && toMoment(config.end_date).utc();
        const days = config.days;

        if (!startDate || !endDate || startDate > endDate) {
          return false;
        }

        const range = moment.duration(startDate.diff(endDate)).days();
        const daysInRange = [];

        while (range < WEEK_DAYS.length && startDate.isSameOrBefore(endDate)) {
          daysInRange.push(startDate.day());
          startDate.add(1, 'day');
        }

        return (
          days &&
          !days.every((day) => daysInRange.includes(WEEK_DAYS.indexOf(day)))
        );
      }),
  );

  public timeRangeOverlappingValidator: Function = this.createValidator(
    'timeRangeOverlappingValidator',
    (collection) =>
      collection &&
      collection.every((dayPart) =>
        collection
          .filter((filtered) => !isEqual(dayPart, filtered))
          .every(
            ({ start_time: start, end_time: end }) =>
              dayPart.start_time > end ||
              (end >= start && dayPart.end_time < start),
          ),
      ),
  );

  public dayFillingValidator: Function = this.createValidator(
    'dayFillingValidator',
    (collection) => {
      const minutesInADay = moment.duration(1, 'days').asMinutes();

      if (!collection.length) {
        return true;
      }

      const intervals = collection
        .map(({ start_time: start, end_time: end }) =>
          moment
            .duration(toMoment(end).diff(toMoment(start)))
            .add(1, 'minute')
            .asMinutes(),
        )
        .map((value) => (value > 0 ? value : value + minutesInADay));

      return sum(intervals) === minutesInADay;
    },
  );

  /**
   * @deprecated this is not used?
   */
  public uniqueJobCodeValidator: Function = this.createValidator(
    'singleJobCode',
    (actual: any[]) => {
      if (isEmpty(actual)) return true;
      const uniqJobCodes = uniqBy(actual, 'job_code_entity_id');

      return uniqJobCodes.length === actual.length;
    },
  );

  /**
   * Run controls (provided list) revalidation after form update/change
   *
   * @param fieldList: string[]
   * @param subscriptions: Subscription[]
   * @param form: FormGroup
   * @returns void
   */
  public static linkedFieldsValidation(
    fieldList: string[],
    form: FormGroup,
  ): Subscription {
    return form.valueChanges.pipe(rx.debounceTime(0)).subscribe(() =>
      fieldList
        .map((fieldPath: string) => form.get(fieldPath))
        // drop values where control can not be acquired
        .filter(Boolean)
        .forEach((control: AbstractControl) => {
          const errors = isFunction(control.validator)
            ? control.validator(control)
            : null;

          return control.setErrors(errors);
        }),
    );
  }

  /**
   * Return validator that revalidate other dependent control
   *
   * @param fieldPath: string
   * @returns Function
   */
  public static linkFieldValidation(fieldPath: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const linkedControl: AbstractControl = control.root.get(fieldPath);

      if (linkedControl) {
        // In that phase, we have old form value that's why we need that fix
        setTimeout(() => {
          const errors = isFunction(linkedControl.validator)
            ? linkedControl.validator(linkedControl)
            : null;

          linkedControl.setErrors(errors);
        }, 0);
      }

      return null;
    };
  }

  // Time formatter helper
  public static formatTime(time: string): string {
    if (!time) return 'EMPTY';
    const hours: number = +time.split(':')[0];
    const pmTime: string = time.replace(/\d{1,2}/, `${hours - 12}`);

    return hours <= 12 ? `${time} AM` : `${pmTime} PM`;
  }

  /**
   * Return errors provided by ValidationService from form control
   *
   * @returns
   */
  public static getErrorsFormControl(control): any[] {
    let result: any[] = [];
    if (control.dirty && control.errors) {
      result = Object.keys(control.errors)
        .map((key) => control.errors[key])
        .filter((error) => typeof error !== 'boolean');
    }
    if (control instanceof FormGroup || control instanceof FormArray) {
      result = result.concat(ValidationService.getErrorsFormGroup(control));
    }

    return result;
  }

  /**
   * Return errors provided by ValidationService from form control
   *
   * @param group: FormGroup | FormArray
   * @returns
   */
  public static getErrorsFormGroup(group: FormGroup | FormArray): any[] {
    let result: any[] = [];
    let groupErrors: any[] = [];
    let controlsErrors: any[] = [];
    if (group.errors) {
      groupErrors = Object.keys(group.errors)
        .map((key) => group.errors[key])
        .filter((error) => typeof error !== 'boolean');
    }
    if (!group.valid && group.controls) {
      controlsErrors = Object.keys(group.controls)
        .map((key) => group.controls[key])
        .map((control) => ValidationService.getErrorsFormControl(control))
        .filter((errors) => errors.length)
        .reduce((acc, errors) => acc.concat(errors), []);
      result = [...groupErrors, ...controlsErrors];
    }

    return result;
  }

  /**
   * Get error text from control's errors
   *
   * @param errors: ValidationErrors
   * returns string | null
   */
  public static getTextFromControlErrors(
    errors: ValidationErrors = {},
    options: { i18nService: XVII18nService },
  ): string {
    if (!errors) {
      return null;
    }
    const errorToShow = head(values(errors));
    let errorMessage =
      isString(errorToShow) && !isEmpty(errorToShow)
        ? errorToShow
        : I18n.Forms.FormValidations.ValidationError;

    if (!isEmpty(errorToShow) && !isEmpty(get(errorToShow, 'message'))) {
      errorMessage = get(errorToShow, 'message');
    }

    return options.i18nService.instant(errorMessage);
  }

  /**
   * Transforms API error object to a unified error object with code and message
   *
   * @param apiErrors
   * returns { XVIError }
   */
  public static apiErrorMapper(apiErrors): XVIError {
    if (!apiErrors) return null;

    if (apiErrors instanceof ProgressEvent) {
      // Lambda Timeout Error
      return new XVIError({
        code: I18n.ErrorMessages.APIRequestTimeoutTitle,
        message: I18n.ErrorMessages.XVIAPIRequestError,
      });
    }

    return new XVIError(
      Object.assign(
        {
          code: I18n.Common.none,
          message: I18n.ErrorMessages.UnexpectedError,
        },
        pick(apiErrors, ['code', 'message']),
        pick(apiErrors.meta, ['code', 'message']),
      ),
    );
  }

  /*
   Preparing error message to show
   */
  public static prepareErrorMessage(
    field,
    formRules,
    error,
    options: { i18nService: XVII18nService },
  ): XVIError {
    if (!field) {
      return new XVIError({
        message: error.message,
        code: error.code,
      });
    }

    const groups: IFormGroupRules[] = get(formRules, 'groups', []);
    const groupId: string = get(find(groups, { fields: [field] }), 'id', field);

    /*
     Function for getting section name if field name
     in group with "name" isn't same as in group with "id"
     (for example "liability_info.pay_type_entity_id")
    */
    const getSectionNameByGroupIdAsField = (): string =>
      get(
        find(groups, { fields: [groupId] }),
        'name',
        get(
          find(groups, { id: 'default' }),
          'name',
          options.i18nService.instant(I18n.Common.General),
        ),
      );

    const sectionName: string = get(
      find(groups, { fields: [field] }),
      'name',
      getSectionNameByGroupIdAsField(),
    );

    const indexOfNesting: number = field.lastIndexOf('.');
    const title = get(formRules, ['fields', field, 'titleObs'], {});
    const fieldName: string = title.getValue
      ? title.getValue()
      : get(
          formRules,
          ['fields', field, 'title'],
          startCase(
            indexOfNesting === -1 ? field : field.slice(indexOfNesting),
          ),
        );

    const fieldNamePart = fieldName
      ? `${FORM_ERROR_SEPARATOR}"${fieldName}"`
      : '';
    const errorDefinition: IErrorDefinition = {
      message: `"${sectionName}"${fieldNamePart} - ${error.message} \n`,
      code: error.code,
    };

    return new XVIError(errorDefinition);
  }

  public static getPreparedErrorMessage(
    path,
    formRules,
    errors,
    options: { i18nService: XVII18nService },
  ): XVIError {
    const error: IErrorDefinition = new XVIError({
      code: options.i18nService.instant(
        I18n.Forms.FormValidations.FormValidation,
      ),
      message: ValidationService.getTextFromControlErrors(errors, options),
    });

    return ValidationService.prepareErrorMessage(
      path,
      formRules,
      error,
      options,
    );
  }

  /**
   * searches for error details in FormGroup or FormComponent
   */
  public static findFormError(
    formControl,
    formRules,
    options: { i18nService: XVII18nService },
  ): XVIError {
    const rootFormErrors = get(formControl, 'errors');

    if (rootFormErrors) {
      return ValidationService.getPreparedErrorMessage(
        '',
        formRules,
        rootFormErrors,
        options,
      );
    }

    const iter = (
      obj: Record<string, AbstractControl>,
      path = '',
    ): any | Error => {
      for (const [fieldName, control] of Object.entries(obj)) {
        const fullPath = path ? `${path}.${fieldName}` : fieldName;

        if (control.errors) {
          return ValidationService.getPreparedErrorMessage(
            fullPath,
            formRules,
            control.errors,
            options,
          );
        }

        if (has(control, 'controls')) {
          const error: any = iter(control['controls'], fullPath);
          if (error) return error;
        }
      }
    };

    return iter(get(formControl, 'controls')) || '';
  }

  public static getAllFormErrorsRecursively(
    formControl: AbstractControl,
    formRules: IFormRules,
    options: { i18nService: XVII18nService },
  ): XVIError[] {
    const formErrors: XVIError[] = [];

    const iterator = (
      control: FormGroup | AbstractControl,
      path: string = FORM_ROOT_PATH,
    ): XVIError[] => {
      if (control.disabled) {
        return;
      }

      if (control.errors) {
        formErrors.push(
          ValidationService.getPreparedErrorMessage(
            path,
            formRules,
            control.errors,
            options,
          ),
        );
      }

      if (has(control, 'controls')) {
        for (const [nestedControlPath, nestedControl] of toPairs(
          control['controls'],
        )) {
          const fullPath = !isEmpty(path)
            ? `${path}.${nestedControlPath}`
            : nestedControlPath;
          iterator(nestedControl as any, fullPath);
        }
      }

      return formErrors;
    };

    return iterator(formControl);
  }

  /**
   * Method returns validator that checks code ranges fields whether Range Start more that Range End
   * and values for these fields are numbers. It returns null if code type selected as "code"
   */
  public static getCodeRangesValidator(options: {
    i18nService: XVII18nService;
  }): ValidatorFn {
    return (form: FormGroup): IValidationError | null => {
      const codeTypeValue = getFormFieldValue(form, 'code_type');
      if (codeTypeValue === 'code') return null;

      const stringRangeStartValue: string = getFormFieldValue(
        form,
        'range_start',
      );
      const stringRangeEndValue: string = getFormFieldValue(form, 'range_end');
      if (isEmpty(stringRangeStartValue) || isEmpty(stringRangeEndValue)) {
        return null;
      }

      const rangeStartValue: number = toNumber(stringRangeStartValue);
      const rangeEndValue: number = toNumber(stringRangeEndValue);

      if (isNaN(rangeStartValue) || isNaN(rangeEndValue)) {
        return {
          errorMessage: options.i18nService.translate(
            I18n.ErrorMessages.FieldValueIsNotNumber,
          ),
        };
      }

      if (rangeStartValue > rangeEndValue) {
        return {
          errorMessage: options.i18nService.translate(
            I18n.ErrorMessages.RangeStartShouldNotBeGreaterThanRangeEnd,
          ),
        };
      }
    };
  }
  public nutritionalRangeValidator: Function = (predicate, mode): any =>
    this.createValidator(
      'invalidNutritionalRangeValues',
      (actualList) => !actualList.some((item) => predicate(item, mode)),
    )();

  public nutritionalValuesValidator: Function = (predicate, mode): any =>
    this.createValidator(
      'invalidNutritionalValues',
      (actualList) => !actualList.some((item) => predicate(item, mode)),
    )();

  public constructor(public i18nService: XVII18nService) {
    this.ajv = new Ajv({
      extendRefs: true, // optional, current default is to 'fail', spec behaviour is to 'ignore'
      unknownFormats: 'ignore', // optional, current default is true (fail)
    });
    // this.ajv.addMetaSchema(metaSchema);
    // optional, using unversioned URI is out of spec,
    // see https://github.com/json-schema-org/json-schema-spec/issues/216
    // Optionally you can also disable keywords defined in draft-06
    this.ajv.removeKeyword('propertyNames');
    this.ajv.removeKeyword('contains');
    this.ajv.removeKeyword('const');
  }

  /*
   * Is used for url validation or empty string
   */
  public urlValidator = (): ValidatorFn => {
    const regexValidator: ValidatorFn = Validators.pattern(URL_PATTERN);

    return (control: AbstractControl): ValidationErrors =>
      !regexValidator(control)
        ? null
        : {
            invalidURL: this.i18nService.translate(
              I18n.ErrorMessages.UrlFormatIsNotCorrect,
            ),
          };
  };

  /*
   * Is used for url array validation (form value is array of strings)
   */
  public urlArrayValidator = (): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors => {
      const isFormatValid = every(control.value, (url) =>
        URL_PATTERN.test(url),
      );
      if (!isFormatValid && !isEmpty(control.value)) {
        return {
          invalidURL: this.i18nService.translate(
            I18n.ErrorMessages.UrlFormatIsNotCorrect,
          ),
        };
      }
      const isUnique = new Set(control.value).size === control.value.length;

      return isUnique
        ? null
        : {
            invalidURL: this.i18nService.translate(
              I18n.ErrorMessages.UrlIsNotUnique,
            ),
          };
    };
  };

  public checkDates(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let row: string = '';
      const validationResult: any = control.value.every((element, index) => {
        const start: any = element.start_business_date_time;
        const end: any = element.end_business_date_time;
        if (!!start && !!end && start <= end) {
          return true;
        }
        row = index + 1;

        return false;
      });

      return validationResult
        ? null
        : {
            // eslint-disable-next-line
            checkDates: this.messages.wrongDates(row),
            // eslint-disable-next-line
          };
    };
  }

  /**
   * Is used to attach error class if control is touched and valid.
   *
   * @param control: FormControl
   * @returns
   */
  public getErrorClass(control): ValidationErrors {
    return {
      'has-error': !control.valid,
    };
  }

  public jsonSchemaValidator(): ValidatorFn {
    return (control: AbstractControl): any => {
      const value: any = control.value;
      let jsonValue: any;

      try {
        jsonValue = JSON.parse(value);
      } catch (e) {
        return {
          jsonSchemaValidator: this.i18nService.translate(
            I18n.ErrorMessages.JsonParseIsNotValid,
          ),
        };
      }

      if (!this.ajv.validateSchema(jsonValue)) {
        return {
          jsonSchemaValidator: this.i18nService.translate(
            I18n.ErrorMessages.JsonCompileIsNotValid,
          ),
        };
      }

      return null;
    };
  }

  public atLeastOneRequired(formGroup: FormGroup): any {
    const valid: boolean = Object.values(formGroup.controls).some(
      (control) => control.value,
    );

    return valid
      ? null
      : {
          // eslint-disable-next-line
          atleastOneRequired: this.messages.atLeastOneFieldRequired(
            formGroup.value,
            Object.keys(formGroup.controls).join(', '),
          ),
          // eslint-disable-next-line
        };
  }

  // A wrapper function that provides an ability to check inner
  // object properties with any existing validator
  public propertyAt(propertyPath, validator): ValidatorFn {
    return (control: AbstractControl): any => {
      const value: any = get(control.value, propertyPath);

      return validator({ value });
    };
  }

  public filteredArrayValidator({ predicate, validator }): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = filter(control.value, predicate);

      return validator({ value } as AbstractControl);
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public arrayUniqValidator(errorMessage?: string): ValidatorFn {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return (control: AbstractControl): ValidationErrors | null => {
      return null;
    };
  }

  public hasEmptyValues(errorMessage?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      arrayHasEmptyValues(control.value)
        ? {
            error:
              errorMessage ||
              this.i18nService.translate(
                I18n.ErrorMessages.PleaseAddAtLeastOneOption,
              ),
          }
        : null;
  }

  public requiredConfigurationSetsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors =>
      !isBoolean(control.value) && isEmpty(control.value)
        ? {
            error: this.i18nService.translate(
              I18n.ErrorMessages.AtLeastOneSetShouldBePresent,
            ),
          }
        : null;
  }

  /**
   * @deprecated is not used?
   */
  public configurationSetsNameValidator(params: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const configSetValue = this.getConfigurationSetValue({
        params,
        controlValue: control.value,
      });

      if (isEmpty(configSetValue)) return null;

      const isNotAllSetsHaveName = some(configSetValue, (set) =>
        isEmpty(get(set, params.fieldLabelPath)),
      );

      return isNotAllSetsHaveName
        ? {
            error: this.i18nService.translate(
              I18n.ErrorMessages.EachSetShouldHaveAName,
            ),
          }
        : null;
    };
  }

  public emptyTrimmedStringInCollection(
    field: string,
    error: string = I18n.ErrorMessages.FieldIsRequired,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const hasEmptyValue = some(
        control.value,
        (item) => !(item[field] ?? '').trim().length,
      );

      return hasEmptyValue ? { error: this.i18nService.instant(error) } : null;
    };
  }

  public isEffectivePrecisionNumber(
    receivedValue: number,
    step: number,
  ): boolean {
    const receivedDecimalPrecision: number = getDecimalPlaces(receivedValue);
    const preferencesDecimalPrecision: number = getDecimalPlaces(step);

    return gte(preferencesDecimalPrecision, receivedDecimalPrecision);
  }

  public outOfPrecisionCurrencyData: Function = this.createValidator(
    'outOfPrecisionCurrencyData',
    ValidationService.decimalPrecisionComparator,
  );

  public buildDynamicMessage(
    dynamicMessage: any,
    actual?: any,
    expected?: any,
  ): string {
    return errorResolver(dynamicMessage, actual, expected);
  }

  private getConfigurationSetValue({
    params,
    controlValue,
  }: {
    params: any;
    controlValue: any;
  }): any {
    const form: FormControl = params.editor?.form;
    if (!form) return null;

    return controlValue;
  }
  // eslint-disable-next-line max-lines
}
